import { useState, useCallback } from 'react';

interface EdgeVectorDocument {
  _id?: string;
  name?: string;
  email?: string;
  createdAt?: string;
  status?: 'active' | 'inactive';
  metadata?: Record<string, any>;
}

interface SearchResult {
  document: EdgeVectorDocument;
  score?: number;
}

const API_BASE = 'https://edgevector-db.finhub.workers.dev';

export const useEdgeVector = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apiRequest = useCallback(async (endpoint: string, options: RequestInit = {}) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}${endpoint}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${process.env.REACT_APP_EDGEVECTOR_API_KEY || ''}`,
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} - ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const healthCheck = useCallback(async () => {
    return apiRequest('/api/v1/health');
  }, [apiRequest]);

  const createDocument = useCallback(
    async (collection: string, document: Omit<EdgeVectorDocument, '_id' | 'createdAt'>) => {
      return apiRequest('/api/v1/documents', {
        method: 'POST',
        body: JSON.stringify({
          collection,
          document: {
            ...document,
            createdAt: new Date().toISOString(),
          },
        }),
      });
    },
    [apiRequest],
  );

  const findDocuments = useCallback(
    async (collection: string, query: Record<string, any> = {}) => {
      const params = new URLSearchParams({
        collection,
        query: JSON.stringify(query),
      });

      return apiRequest(`/api/v1/documents?${params}`);
    },
    [apiRequest],
  );

  const updateDocument = useCallback(
    async (collection: string, documentId: string, updates: Partial<EdgeVectorDocument>) => {
      return apiRequest('/api/v1/documents', {
        method: 'PATCH',
        body: JSON.stringify({
          collection,
          documentId,
          updates: {
            ...updates,
            updatedAt: new Date().toISOString(),
          },
        }),
      });
    },
    [apiRequest],
  );

  const deleteDocument = useCallback(
    async (collection: string, documentId: string) => {
      return apiRequest('/api/v1/documents', {
        method: 'DELETE',
        body: JSON.stringify({
          collection,
          documentId,
        }),
      });
    },
    [apiRequest],
  );

  const vectorSearch = useCallback(
    async (
      collection: string,
      params: {
        text?: string;
        vector?: number[];
        limit?: number;
      },
    ) => {
      return apiRequest('/api/v1/vectors/search', {
        method: 'POST',
        body: JSON.stringify({
          collection,
          ...params,
        }),
      });
    },
    [apiRequest],
  );

  return {
    // State
    loading,
    error,

    // Methods
    healthCheck,
    createDocument,
    findDocuments,
    updateDocument,
    deleteDocument,
    vectorSearch,

    // Utilities
    clearError: () => setError(null),
  };
};
